/**
 * Token amounts: base units (decimal strings from the API, `bigint` in code)
 * to the words the pages print, and typed text back to base units. Exact:
 * no float ever stands for an amount that is sent.
 */

import { formatTokenUnits, fromBaseUnits, toBaseUnits } from "./amount";

/** At or above this, an allowance reads "Unlimited" (an approve of 2^256 - 1, or what is left of it). */
export const UNLIMITED_FLOOR = 1n << 255n;
export const MAX_UINT256 = (1n << 256n) - 1n;

/** "18,213.12" (two decimals, exact); "—" for a missing or malformed amount. */
export function formatUnitsText(raw: string | null | undefined, decimals: number): string {
  if (raw === null || raw === undefined) return "—";
  return formatTokenUnits(raw, decimals) ?? "—";
}

/** "18,213.12 USDC". */
export function formatToken(raw: string | null | undefined, decimals: number, symbol: string): string {
  const text = formatUnitsText(raw, decimals);
  return text === "—" ? text : `${text} ${symbol}`;
}

/** An allowance: "Unlimited", "None" for zero, else "250,000.00 USDC"; "—" when unknown. */
export function formatAllowance(raw: string | null | undefined, decimals: number, symbol: string): string {
  if (raw === null || raw === undefined || !/^\d+$/.test(raw)) return "—";
  const v = BigInt(raw);
  if (v >= UNLIMITED_FLOOR) return "Unlimited";
  if (v === 0n) return "None";
  return formatToken(raw, decimals, symbol);
}

/** Whole tokens as a float, for display arithmetic only (USD values, shares). */
export function toFloat(raw: string | null | undefined, decimals: number): number | null {
  if (raw === null || raw === undefined) return null;
  const text = fromBaseUnits(raw, decimals);
  return text === null ? null : Number(text);
}

/** "500,000" or "50,000.5": what an input shows for an amount in force, thousands grouped, nothing rounded. */
export function inputText(raw: string, decimals: number): string {
  const text = fromBaseUnits(raw, decimals);
  if (text === null) return "";
  const [whole = "0", frac] = text.split(".");
  const grouped = BigInt(whole).toLocaleString("en-US");
  return frac === undefined ? grouped : `${grouped}.${frac}`;
}

/** Why typed text is not an amount of this token, or null when it is. */
export function amountRefusal(text: string, decimals: number): string | null {
  const cleaned = text.replace(/[,\s_']/g, "");
  if (cleaned === "") return "Enter an amount.";
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === ".") return "Enter a number, such as 50,000.";
  const frac = cleaned.split(".")[1] ?? "";
  if (frac.length > decimals) return decimals === 0 ? "Whole units only." : `At most ${decimals} decimals.`;
  return null;
}

/** Typed text in base units (null when refused). */
export function parseAmount(text: string, decimals: number): bigint | null {
  if (amountRefusal(text, decimals) !== null) return null;
  const base = toBaseUnits(text, decimals);
  return base === null ? null : BigInt(base);
}

/** Typed allowance text in base units: "Unlimited" is 2^256 - 1. */
export function parseAllowance(text: string, decimals: number): { amount: bigint } | { refusal: string } {
  if (text.trim().toLowerCase() === "unlimited") return { amount: MAX_UINT256 };
  const refusal = amountRefusal(text, decimals);
  if (refusal !== null) return { refusal: refusal === "Enter an amount." ? "Enter an amount, Unlimited or 0." : refusal };
  const amount = parseAmount(text, decimals);
  return amount === null ? { refusal: "Enter an amount, Unlimited or 0." } : { amount };
}

const usdFormatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "$68,540.20" from a decimal string or a number; "—" when unknown. */
export function formatUsd(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? `$${usdFormatter.format(n)}` : "—";
}

/** "250,000": a whole number grouped. */
export function formatInteger(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

/** Tokens counted as one US dollar each (the USD-pegged leg of a pair), as the API counts them. */
const USD_PEGGED = new Set(["USDC", "USDT", "USDBC", "DAI", "FDUSD"]);

export function isUsdPegged(symbol: string): boolean {
  return USD_PEGGED.has(symbol.toUpperCase());
}
